/* Carbon Performance — apparel storefront JS. No dependencies. Uses Shopify's Cart AJAX API,
   Section Rendering API and predictive search. Loaded as a module from snippets/scripts.liquid. */
(() => {
  'use strict';
  const cfg = window.CarbonStore || {};
  const routes = cfg.routes || {};
  const S = cfg.strings || {};
  const DRAWER_SECTION = 'carbon-cart-drawer';
  const q = (sel, root = document) => root.querySelector(sel);
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const tpl = (str, vars) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (vars && vars[k] != null ? vars[k] : m));
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ---------- money ---------- */
  const formatMoney = (cents, format) => {
    const fmt = format || cfg.moneyFormat || '${{amount}}';
    const value = Number(cents) || 0;
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = m ? m[1] : 'amount';
    const withDelims = (n, precision, thousands, decimal) => {
      const [ints, dec] = (n / 100).toFixed(precision).split('.');
      const grouped = ints.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? grouped + decimal + dec : grouped;
    };
    let out;
    switch (type) {
      case 'amount_no_decimals': out = withDelims(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = withDelims(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelims(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = withDelims(value, 2, "'", '.'); break;
      case 'amount_with_space_separator': out = withDelims(value, 2, ' ', ','); break;
      default: out = withDelims(value, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  };

  /* ---------- scroll helpers ---------- */
  let locks = 0;
  const lockScroll = () => { locks += 1; document.documentElement.setAttribute('scroll-lock', ''); };
  const unlockScroll = () => { locks = Math.max(0, locks - 1); if (!locks) document.documentElement.removeAttribute('scroll-lock'); };
  const scroller = () => { const pw = q('.page-wrapper'); return pw && /auto|scroll/.test(getComputedStyle(pw).overflowY) ? pw : null; };
  const scrollToEl = (el, offset = 110) => {
    const top = el.getBoundingClientRect().top - offset;
    const sc = scroller();
    (sc || window).scrollBy({ top, behavior: 'smooth' });
  };

  /* ---------- toast ---------- */
  const toast = (() => {
    let el; let timer;
    const ensure = () => {
      if (el) return el;
      el = document.createElement('div');
      el.className = 'cs-toast'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite'); el.hidden = true;
      document.body.appendChild(el);
      return el;
    };
    return (msg, action) => {
      const t = ensure(); clearTimeout(timer); t.innerHTML = '';
      const span = document.createElement('span'); span.textContent = msg; t.appendChild(span);
      if (action) {
        const b = document.createElement('button'); b.type = 'button'; b.textContent = action.label;
        b.addEventListener('click', () => { t.hidden = true; action.onClick(b); });
        t.appendChild(b);
      }
      const x = document.createElement('button'); x.type = 'button'; x.className = 'cs-toast__x'; x.setAttribute('aria-label', S.dismiss || 'Dismiss');
      x.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
      x.addEventListener('click', () => { t.hidden = true; });
      t.appendChild(x);
      t.hidden = false;
      timer = setTimeout(() => { t.hidden = true; }, 5000);
    };
  })();

  /* ---------- overlay base (menu drawer, search, cart) ---------- */
  class CsOverlay extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cs-close]');
        if (!t || !this.contains(t)) return;
        if (t.tagName !== 'A') e.preventDefault();
        this.close();
      });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
    }
    get isOpen() { return this.hasAttribute('open'); }
    open(trigger) {
      if (this.isOpen) return;
      qa('[open].cs-overlay').forEach((o) => { if (o !== this && typeof o.close === 'function') o.close({ restoreFocus: false }); });
      this._trigger = trigger || document.activeElement;
      this.setAttribute('open', '');
      lockScroll();
      qa('[aria-controls="' + this.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        const f = q('[data-cs-autofocus]', this) || qa('[data-cs-close]', this).find((el) => !el.classList.contains('cs-overlay__backdrop')) || q(FOCUSABLE, this);
        if (f) f.focus({ preventScroll: true });
        if (typeof this.onOpen === 'function') this.onOpen();
      });
    }
    close(opts = {}) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      unlockScroll();
      qa('[aria-controls="' + this.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (typeof this.onClose === 'function') this.onClose();
      const t = this._trigger; this._trigger = null;
      if (opts.restoreFocus !== false && t && typeof t.focus === 'function') setTimeout(() => t.focus({ preventScroll: true }), 0);
    }
    onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const f = qa(FOCUSABLE, this).filter((el) => el.offsetParent !== null && !el.classList.contains('cs-overlay__backdrop'));
      if (!f.length) return;
      const first = f[0]; const last = f[f.length - 1]; const a = document.activeElement;
      if (e.shiftKey && (a === first || !this.contains(a))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (a === last || !this.contains(a))) { e.preventDefault(); first.focus(); }
    }
  }
  if (!customElements.get('cs-overlay')) customElements.define('cs-overlay', CsOverlay);

  /* ---------- cart API ---------- */
  const setCartCount = (n) => {
    qa('[data-cs-cart-count]').forEach((el) => { el.textContent = n; });
    qa('[data-cs-bag-button]').forEach((b) => b.setAttribute('aria-label', tpl(S.bagCount || 'Bag, [count] items', { count: n })));
  };
  async function cartRequest(url, body) {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    let data = {};
    try { data = await res.json(); } catch (e) { /* non-JSON */ }
    if (!res.ok) {
      const err = new Error(data.description || data.message || S.genericError || 'Something went wrong. Please try again.');
      err.status = res.status; err.data = data; throw err;
    }
    return data;
  }
  const cartSectionIds = () => {
    const ids = [DRAWER_SECTION];
    const page = q('[data-cs-cart-page]');
    if (page && page.dataset.csSectionId) ids.push(page.dataset.csSectionId);
    return ids;
  };
  let cartQueue = Promise.resolve();
  let cartNeedsPage = false;
  function applySections(sections) {
    if (!sections) return false;
    const swap = (html, container) => {
      if (!container) return true;
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = q('[data-cs-cart-body]', doc); const cur = q('[data-cs-cart-body]', container);
      if (!fresh || !cur) return false;
      cur.replaceWith(fresh);
      const count = fresh && fresh.getAttribute('data-cs-cart-total-count');
      if (count != null) setCartCount(count);
      return true;
    };
    const drawerUpdated = swap(sections[DRAWER_SECTION], q('[data-cs-cart-drawer]'));
    const page = q('[data-cs-cart-page]');
    const pageUpdated = !page || swap(sections[page.dataset.csSectionId], page);
    return drawerUpdated && pageUpdated;
  }
  async function refreshCart() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', cartSectionIds().join(','));
      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) throw new Error('Cart display could not be refreshed');
      cartNeedsPage = !applySections(await res.json());
    } catch (err) { cartNeedsPage = true; }
    if (cartNeedsPage && q('[data-cs-cart-page]')) window.location.assign(routes.cart || '/cart');
  }
  function mutateCart(url, body) {
    // Serialize mutations so a slower response cannot overwrite a newer cart.
    const result = cartQueue.then(async () => {
      try {
        const data = await cartRequest(url, Object.assign({}, body, { sections: cartSectionIds().join(','), sections_url: window.location.pathname }));
        cartNeedsPage = !applySections(data.sections);
        if (cartNeedsPage) await refreshCart();
        return data;
      } catch (err) {
        // A stock error can still add the available quantity. Read the cart back;
        // never retry the POST, which could add the item a second time.
        await refreshCart();
        throw err;
      }
    });
    cartQueue = result.catch(() => {});
    return result;
  }
  const addToCart = (id, quantity) => mutateCart(routes.cartAdd || '/cart/add.js', { items: [{ id: Number(id), quantity: Number(quantity) || 1 }] });
  const changeLine = (key, quantity) => mutateCart(routes.cartChange || '/cart/change.js', { id: key, quantity });
  const openCart = (trigger) => { const d = q('#CsCart'); if (!cartNeedsPage && d && typeof d.open === 'function') d.open(trigger); else window.location.href = routes.cart || '/cart'; };

  /* ---------- cart drawer ---------- */
  class CsCartDrawer extends CsOverlay {
    connectedCallback() {
      super.connectedCallback();
      if (this._cartBound) return; this._cartBound = true;
      this.addEventListener('click', (e) => this.onLineClick(e));
    }
    async onLineClick(e) {
      const btn = e.target.closest('[data-cs-line-qty]');
      if (!btn || !this.contains(btn)) return;
      e.preventDefault();
      await handleLineChange(btn, this);
    }
  }
  if (!customElements.get('cs-cart-drawer')) customElements.define('cs-cart-drawer', CsCartDrawer);

  async function handleLineChange(btn, container) {
    const key = btn.dataset.csKey; const qty = Number(btn.dataset.csLineQty);
    const cart = q('.cs-cart', container) || container;
    if (cart.getAttribute('aria-busy') === 'true') return;
    cart.setAttribute('aria-busy', 'true');
    qa('[data-cs-line-error]', container).forEach((p) => { p.hidden = true; p.textContent = ''; });
    try { await changeLine(key, qty); }
    catch (err) {
      const line = q('[data-cs-line="' + key + '"]', container);
      const p = line && q('[data-cs-line-error]', line);
      if (p) { p.textContent = err.message; p.hidden = false; } else toast(err.message);
    } finally {
      const fresh = q('.cs-cart', container) || container; fresh.removeAttribute('aria-busy');
    }
  }
  // Cart page (same markup, not an overlay)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cs-cart-page] [data-cs-line-qty]');
    if (btn) { e.preventDefault(); handleLineChange(btn, btn.closest('[data-cs-cart-page]')); }
  });

  /* ---------- header triggers ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cs-open]');
    if (!btn) return;
    const target = document.getElementById(btn.dataset.csOpen);
    if (btn.dataset.csOpen === 'CsCart') { e.preventDefault(); openCart(btn); }
    else if (target && typeof target.open === 'function') { e.preventDefault(); target.open(btn); }
  });

  /* ---------- search ---------- */
  class CsSearch extends CsOverlay {
    connectedCallback() {
      super.connectedCallback();
      if (this._searchBound) return; this._searchBound = true;
      this.input = q('[data-cs-search-input]', this);
      this.status = q('[data-cs-search-status]', this);
      this.idle = q('[data-cs-search-idle]', this);
      this.results = q('[data-cs-search-results]', this);
      this.empty = q('[data-cs-search-empty]', this);
      this.emptyText = q('[data-cs-search-empty-text]', this);
      this.template = q('[data-cs-result-template]', this);
      this.active = -1; this.items = [];
      if (!this.input) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this._timer);
        this._pending = ''; this.items = []; this.active = -1;
        this.setState('loading');
        if (this.input.value.trim().length < 2) this.run(this.input.value);
        else this._timer = setTimeout(() => this.run(this.input.value), 250);
      });
      this.input.addEventListener('keydown', (e) => this.onInputKey(e));
      const form = q('[data-cs-search-form]', this);
      if (form) form.addEventListener('submit', (e) => { if (!this.input.value.trim()) e.preventDefault(); });
      this.results.addEventListener('mouseover', (e) => { const li = e.target.closest('[role="option"]'); if (li) this.setActive(this.items.indexOf(li)); });
    }
    onOpen() { if (this.input) { this.input.focus({ preventScroll: true }); this.input.select && this.input.select(); } }
    onClose() { clearTimeout(this._timer); this._pending = ''; }
    setState(state) {
      if (this.idle) this.idle.hidden = state !== 'idle';
      this.results.hidden = state !== 'results';
      this.empty.hidden = state !== 'empty';
    }
    setActive(i) {
      this.active = i;
      this.items.forEach((li, idx) => li.setAttribute('aria-selected', idx === i ? 'true' : 'false'));
    }
    onInputKey(e) {
      const n = this.items.length;
      if (e.key === 'ArrowDown' && n) { e.preventDefault(); this.setActive((this.active + 1) % n); }
      else if (e.key === 'ArrowUp' && n) { e.preventDefault(); this.setActive((this.active - 1 + n) % n); }
      else if (e.key === 'Enter' && this.active >= 0 && this.items[this.active]) {
        e.preventDefault(); const a = q('a', this.items[this.active]); if (a) window.location.href = a.href;
      }
    }
    async run(raw) {
      const term = (raw || '').trim();
      this.items = []; this.active = -1;
      this._pending = term;
      if (term.length < 2) { this.status.textContent = S.searchIdle || ''; this.setState('idle'); return; }
      this.status.textContent = S.searching || 'Searching';
      this.setState('loading');
      try {
        const url = (routes.predictive || '/search/suggest.json') + '?q=' + encodeURIComponent(term) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag';
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('predictive search unavailable');
        const data = await res.json();
        if (this._pending !== term) return;
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        this.render(products, term);
      } catch (err) {
        if (this._pending !== term) return;
        this.status.textContent = S.searchFallback || 'Press Enter to search all products.';
        this.setState('idle');
      }
    }
    render(products, term) {
      this.results.innerHTML = '';
      if (!products.length) {
        this.status.textContent = S.noResults || 'No results';
        if (this.emptyText) this.emptyText.textContent = tpl(S.noResultsHint || 'No results for “[query]”.', { query: term });
        this.setState('empty');
        return;
      }
      products.forEach((p) => {
        const node = this.template.content.firstElementChild.cloneNode(true);
        const a = q('a', node); a.href = p.url;
        const img = q('img', node);
        const src = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
        if (src) { img.src = src.replace(/(\.[a-z]+)(\?|$)/i, '_96x120_crop_center$1$2'); img.alt = (p.featured_image && p.featured_image.alt) || ''; } else img.remove();
        q('.cs-result__title', node).textContent = p.title;
        const price = p.price != null ? formatMoney(Math.round(parseFloat(p.price) * 100)) : '';
        q('.cs-result__meta', node).textContent = price + (p.available === false ? ' · ' + (S.soldOut || 'Sold out') : '');
        this.results.appendChild(node);
      });
      this.items = qa('[role="option"]', this.results);
      this.status.textContent = tpl(S.resultsFor || '[count] results for “[query]”', { count: products.length, query: term });
      this.setState('results');
    }
  }
  if (!customElements.get('cs-search')) customElements.define('cs-search', CsSearch);

  /* ---------- product cards: dots, quick add, tabs ---------- */
  document.addEventListener('click', async (e) => {
    const dot = e.target.closest('[data-cs-dot]');
    if (dot) {
      const card = dot.closest('[data-cs-card]');
      if (card) { card.setAttribute('data-cs-alt', dot.dataset.csDot); qa('[data-cs-dot]', card).forEach((d) => d.setAttribute('aria-pressed', d === dot ? 'true' : 'false')); }
      return;
    }
    const toggle = e.target.closest('[data-cs-quick-toggle]');
    if (toggle) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = toggle.getAttribute('aria-expanded') === 'true';
      qa('[data-cs-quick-toggle][aria-expanded="true"]').forEach((t) => { if (t !== toggle) { t.setAttribute('aria-expanded', 'false'); const p = document.getElementById(t.getAttribute('aria-controls')); if (p) p.hidden = true; } });
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (panel) { panel.hidden = open; if (!open) { const first = q('button:not([disabled])', panel); if (first) first.focus({ preventScroll: true }); } }
      return;
    }
    const add = e.target.closest('[data-cs-quick-add]');
    if (add) {
      e.preventDefault();
      if (add.getAttribute('aria-busy') === 'true') return;
      add.setAttribute('aria-busy', 'true');
      try {
        await addToCart(add.dataset.csVariantId, 1);
        const panel = add.closest('.cs-quick');
        if (panel) { panel.hidden = true; const t = q('[aria-controls="' + panel.id + '"]'); if (t) t.setAttribute('aria-expanded', 'false'); }
        toast(tpl(S.addedToBagNamed || '[title] added to bag', { title: add.dataset.csTitle || '' }), { label: S.viewBag || 'View bag', onClick: (b) => openCart(b) });
      } catch (err) { toast(err.message); }
      finally { add.removeAttribute('aria-busy'); }
    }
  });

  class CsCollectionGrid extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-cs-tab]');
        if (!tab || !this.contains(tab)) return;
        const handle = tab.dataset.csTab;
        qa('[data-cs-tab]', this).forEach((t) => t.setAttribute('aria-pressed', t === tab ? 'true' : 'false'));
        let shown = 0;
        qa('[data-cs-card]', this).forEach((card) => {
          const list = (card.dataset.csCollections || '').split(/\s+/);
          const show = handle === 'all' || list.includes(handle);
          card.hidden = !show; if (show) shown += 1;
          const quick = q('.cs-quick', card); if (quick) quick.hidden = true;
          const qt = q('[data-cs-quick-toggle]', card); if (qt) qt.setAttribute('aria-expanded', 'false');
        });
        const empty = q('[data-cs-tab-empty]', this); if (empty) empty.hidden = shown > 0;
      });
    }
  }
  if (!customElements.get('cs-collection-grid')) customElements.define('cs-collection-grid', CsCollectionGrid);

  /* ---------- product gallery ---------- */
  class CsGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.addEventListener('click', (e) => { const t = e.target.closest('[data-cs-thumb]'); if (t && this.contains(t)) this.show(Number(t.dataset.csThumb)); });
    }
    show(i) {
      const slides = qa('[data-cs-slide]', this); if (!slides.length) return;
      const idx = Math.max(0, Math.min(i, slides.length - 1));
      slides.forEach((s) => { s.hidden = Number(s.dataset.csSlide) !== idx; });
      qa('[data-cs-thumb]', this).forEach((t) => t.setAttribute('aria-pressed', Number(t.dataset.csThumb) === idx ? 'true' : 'false'));
    }
  }
  if (!customElements.get('cs-gallery')) customElements.define('cs-gallery', CsGallery);

  /* ---------- product form ---------- */
  class CsProductForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.form = q('form', this);
      const json = q('[data-cs-variants]', this);
      try { this.variants = json ? JSON.parse(json.textContent) : []; } catch (e) { this.variants = []; }
      this.idInput = q('[data-cs-variant-input]', this);
      this.optionSets = qa('[data-cs-option]', this);
      this.qtyInput = q('[data-cs-qty-input]', this);
      this.atc = q('[data-cs-atc]', this);
      this.atcLabel = q('[data-cs-atc-label]', this);
      this.spinner = q('[data-cs-atc-spinner]', this);
      this.error = q('[data-cs-form-error]', this);
      this.msg = q('[data-cs-opt-msg]', this);
      this.root = this.closest('[data-cs-product]') || document;
      this.sizeUnavailable = this.hasAttribute('data-cs-size-unavailable');
      this.selection = this.optionSets.map(() => null);
      const current = this.variants.find((v) => String(v.id) === String(this.idInput && this.idInput.value));
      if (current) this.selection = current.options.slice();
      this.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-cs-value]');
        if (opt && this.contains(opt)) { const set = opt.closest('[data-cs-option]'); this.select(Number(set.dataset.csOption), opt.dataset.csValue); return; }
        const qty = e.target.closest('[data-cs-qty]');
        if (qty && this.contains(qty)) { this.setQty((Number(this.qtyInput.value) || 1) + Number(qty.dataset.csQty)); }
      });
      if (this.qtyInput) this.qtyInput.addEventListener('change', () => this.setQty(Number(this.qtyInput.value) || 1));
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submitAdd(); });
      qa('[data-cs-atc-proxy]', this.root).forEach((b) => b.addEventListener('click', () => this.submitAdd()));
      this.render();
    }
    get variant() {
      if (!this.optionSets.length) return this.variants[0] || null;
      if (this.selection.some((s) => s == null)) return null;
      return this.variants.find((v) => v.options.every((o, i) => o === this.selection[i])) || null;
    }
    select(index, value) {
      if (this._busy) return;
      this.selection[index] = value;
      this.clearError();
      this.render();
    }
    setQty(n) {
      const v = Math.max(1, Math.min(99, Math.round(n) || 1));
      if (this.qtyInput) this.qtyInput.value = v;
      const dec = q('[data-cs-qty="-1"]', this); if (dec) dec.disabled = v <= 1;
    }
    render() {
      const v = this.variant;
      this.optionSets.forEach((set, i) => {
        qa('[data-cs-value]', set).forEach((btn) => {
          const val = btn.dataset.csValue;
          btn.setAttribute('aria-pressed', this.selection[i] === val ? 'true' : 'false');
          const possible = this.variants.some((vr) => vr.available && vr.options[i] === val && vr.options.every((o, j) => j === i || this.selection[j] == null || o === this.selection[j]));
          if (possible) btn.removeAttribute('data-cs-unavailable'); else btn.setAttribute('data-cs-unavailable', 'true');
        });
      });
      if (this.idInput) this.idInput.value = v ? v.id : '';
      const price = q('[data-cs-price]', this.root); const avail = q('[data-cs-avail]', this.root);
      if (v) {
        if (price) price.innerHTML = (v.compare_at ? '<s>' + v.compare_at + '</s> ' : '') + v.price;
        if (avail) { avail.textContent = v.available ? (S.inStock || 'In stock') : (S.soldOut || 'Sold out'); avail.className = 'cs-avail ' + (v.available ? 'cs-avail--in' : 'cs-avail--out'); }
        if (v.media != null) { const g = q('cs-gallery', this.root); if (g && typeof g.show === 'function') g.show(v.media); }
      }
      if (avail && this.sizeUnavailable) { avail.textContent = S.unavailable || 'Currently unavailable'; avail.className = 'cs-avail cs-avail--out'; }
      if (this.msg && this.optionSets.length) {
        const name = this.msg.dataset.csOptionName || 'size';
        this.msg.textContent = v ? tpl(S.sizeSelected || '[value] selected', { value: v.title }) : tpl(S.selectOption || 'Select a [option]', { option: name });
        this.msg.classList.remove('cs-opt__msg--err');
      }
      const soldOut = v ? !v.available : !this.variants.some((variant) => variant.available);
      const blocked = soldOut || this.sizeUnavailable;
      const blockedLabel = this.sizeUnavailable ? (S.unavailable || 'Currently unavailable') : (S.soldOut || 'Sold out');
      qa('[data-cs-value]', this).forEach((button) => { button.disabled = !!this._busy || this.sizeUnavailable; });
      if (this.atc) { this.atc.disabled = blocked || !!this._busy; if (this.atcLabel && !this._busy) this.atcLabel.textContent = blocked ? blockedLabel : (S.addToBag || 'Add to bag'); }
      const sub = q('[data-cs-sticky-sub]', this.root);
      if (sub && v) sub.textContent = v.price + (this.optionSets.length ? ' · ' + v.title : '');
      qa('[data-cs-atc-proxy]', this.root).forEach((b) => { b.disabled = blocked || !!this._busy; b.textContent = blocked ? blockedLabel : (!v && this.optionSets.length ? tpl(S.selectOption || 'Select a [option]', { option: (this.msg && this.msg.dataset.csOptionName) || 'size' }) : (S.addToBag || 'Add to bag')); });
      if (v && v.media == null && this._initialRender !== false) { /* keep gallery */ }
      this._initialRender = false;
    }
    showError(msg) { if (this.error) { this.error.textContent = msg; this.error.hidden = false; } }
    clearError() { if (this.error) { this.error.hidden = true; this.error.textContent = ''; } }
    async submitAdd() {
      if (this._busy || this.sizeUnavailable || !this.variants.some((variant) => variant.available)) return;
      const v = this.variant;
      if (!v) {
        if (this.msg) { this.msg.textContent = tpl(S.sizeRequired || 'Please select a [option] to continue.', { option: this.msg.dataset.csOptionName || 'size' }); this.msg.classList.add('cs-opt__msg--err'); }
        const set = this.optionSets.find((s, i) => this.selection[i] == null) || this.optionSets[0];
        if (set) { scrollToEl(set); const first = q('[data-cs-value]:not([data-cs-unavailable])', set) || q('[data-cs-value]', set); if (first) first.focus({ preventScroll: true }); }
        return;
      }
      if (!v.available) return;
      this.clearError(); this._busy = true;
      this.render();
      if (this.atc) this.atc.disabled = true;
      if (this.spinner) this.spinner.hidden = false;
      if (this.atcLabel) this.atcLabel.textContent = S.adding || 'Adding…';
      try {
        await addToCart(v.id, this.qtyInput ? Number(this.qtyInput.value) || 1 : 1);
        if (this.spinner) this.spinner.hidden = true;
        if (this.atcLabel) this.atcLabel.textContent = S.added || 'Added to bag';
        if (this.atc) this.atc.classList.add('cs-atc--ok');
        setTimeout(() => {
          if (this.atc) this.atc.classList.remove('cs-atc--ok');
          this._busy = false;
          this.render();
          openCart(this.atc);
        }, 900);
      } catch (err) {
        this._busy = false;
        if (this.spinner) this.spinner.hidden = true;
        this.render();
        this.showError(err.message);
      }
    }
  }
  if (!customElements.get('cs-product-form')) customElements.define('cs-product-form', CsProductForm);

  /* ---------- collection filters ---------- */
  class CsFilters extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.form = q('[data-cs-filter-form]', this);
      if (!this.form) return;
      const mobile = () => window.matchMedia('(max-width: 699px)').matches;
      this.form.addEventListener('change', () => { if (!mobile()) this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit(); });
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cs-filters-open]')) { this.form.setAttribute('data-open', ''); lockScroll(); const c = q('[data-cs-filters-close]', this.form); if (c) c.focus({ preventScroll: true }); qa('[data-cs-filters-open]', this).forEach((b) => b.setAttribute('aria-expanded', 'true')); }
        else if (e.target.closest('[data-cs-filters-close]') || e.target.classList.contains('cs-filterbox__backdrop')) this.closePanel();
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.form.hasAttribute('data-open')) this.closePanel(); });
    }
    closePanel() {
      if (!this.form.hasAttribute('data-open')) return;
      this.form.removeAttribute('data-open'); unlockScroll();
      qa('[data-cs-filters-open]', this).forEach((b) => { b.setAttribute('aria-expanded', 'false'); b.focus({ preventScroll: true }); });
    }
  }
  if (!customElements.get('cs-filters')) customElements.define('cs-filters', CsFilters);

  /* ---------- theme editor hooks ---------- */
  document.addEventListener('shopify:section:select', (e) => { const d = q('cs-cart-drawer', e.target); if (d) d.open(); });
  document.addEventListener('shopify:section:deselect', (e) => { const d = q('cs-cart-drawer', e.target); if (d) d.close({ restoreFocus: false }); });

  window.CarbonStore = Object.assign(window.CarbonStore || {}, { addToCart, changeLine, openCart, toast, formatMoney, setCartCount });
})();
